import { ChoiceButtonInput } from '@app/app/_components/Input/ChoiceButtonInput';
import { TransportInsuranceMessages } from '@app/app/esales/transport/components/TransportInsuranceMessages';
import { ContactUsCard } from '@app/app/_widgets/ContactUsCard';
import { useInsurancePackage } from '@app/app/esales/transport/_sections/setups/useInsurancePackage';
import { TransportPolicyValidity } from '@app/domain/model/eshop/transport/TransportPolicyValidity';
import { TransportPremiumsResponse } from '@app/domain/model/eshop/transport/TransportPremiumsResponse';
import { usePremiumsOptions } from '@app/app/esales/transport/_sections/setups/usePremiumsOptions';
import { ChangeEvent, useEffect, useMemo } from 'react';
import { ArrowRightIcon } from '@app/app/_icons/ArrowRightIcon';
import { Button } from '@app/app/_components/Button/Button';
import { Message } from '@app/app/_components/Message/Message';
import { InsurancePackage } from '@app/domain/model/eshop/transport/InsurancePackage';
import { useTranslations } from 'next-intl';
import { useAppParameters } from '@app/app/_context/AppParametersContext/useAppParameters';
import { PremiumCard } from '@app/app/esales/transport/components/PremiumCard';
import { PremiumCode } from '@app/domain/model/eshop/transport/PremiumCode';
import { Card } from '@app/app/_components/Card/Card';
import { Paragraph } from '@app/app/_components/Paragraph/Paragraph';
import { Heading } from '@app/app/_components/Heading/Heading';
import { InsuranceDuration } from '@app/domain/model/eshop/transport/InsuranceDuration';

interface Props {
    policyValidity: Nullable<TransportPolicyValidity>;
    premiums: TransportPremiumsResponse[];
    setPremiums: (premiums: TransportPremiumsResponse[]) => void;
    insuranceDuration: string;
    onChange?: (e: ChangeEvent<HTMLInputElement>) => void;
    value?: string;
    onPremiumOptionsChange?: (available: boolean) => void;
    isCascoPresent?: boolean;
}

export function ChooseInsuranceStep({
    policyValidity,
    premiums,
    insuranceDuration,
    onChange,
    value,
    onPremiumOptionsChange,
    isCascoPresent,
}: Props) {
    const t = useTranslations();
    const appParameters = useAppParameters();

    const cascoValidity = policyValidity?.cascoPolicyValidity;
    const mtplValidity = policyValidity?.mtplPolicyValidity;

    const premiumOptions = usePremiumsOptions(premiums, insuranceDuration, appParameters, policyValidity);
    const hasPremiums = premiums.length > 0;

    const insurancePackage = useInsurancePackage(cascoValidity ?? null, mtplValidity ?? null, hasPremiums);

    useEffect(() => {
        onPremiumOptionsChange?.(premiumOptions.length > 0);
    }, [premiumOptions.length, onPremiumOptionsChange]);

    const showCompare =
        [
            InsurancePackage.cascoMtplInvalid,
            InsurancePackage.bothValidCascoExpiringSoon,
            InsurancePackage.cascoMtplValidExpiringSoon,
            InsurancePackage.cascoValidExpiringSoonMtplInvalid,
            InsurancePackage.mtplValidExpiringSoonCascoInvalid,
            InsurancePackage.bothValidMtplExpiringSoon,
        ].includes(insurancePackage.status) &&
        hasPremiums &&
        insuranceDuration === InsuranceDuration.TwelveMonths;

    const isMtplValidLongTerm = useMemo(() => {
        if (!mtplValidity?.isValid || !mtplValidity.validUntil) {
            return false;
        }
        const expirationDate = new Date(mtplValidity.validUntil);
        const today = new Date();
        const daysUntilExpiration = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilExpiration >= 30;
    }, [mtplValidity]);

    const isCascoValidLongTerm = useMemo(() => {
        if (!cascoValidity?.isValid || !cascoValidity.validUntil) {
            return false;
        }
        const expirationDate = new Date(cascoValidity.validUntil);
        const today = new Date();
        const daysUntilExpiration = Math.ceil((expirationDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
        return daysUntilExpiration >= 30;
    }, [cascoValidity]);

    const mtplPremium = useMemo(() => {
        return premiums.find(premium => premium.riskPackageCode === PremiumCode.Mtpl && !premium.withCasco);
    }, [premiums]);

    return (
        <div className="flex flex-col gap-8">
            <TransportInsuranceMessages
                status={insurancePackage.status}
                cascoValidity={cascoValidity ?? null}
                mtplValidity={mtplValidity ?? null}
                isCascoPresent={isCascoPresent}
                insuranceDuration={insuranceDuration}
            />

            {mtplPremium && isMtplValidLongTerm && (
                <Card className="p-6">
                    <PremiumCard
                        title={t('eshop.transport.policyType.mtpl')}
                        price={
                            mtplPremium.premiumAfterDiscount > 0 &&
                            mtplPremium.premiumAfterDiscount !== mtplPremium.premium
                                ? mtplPremium.premiumAfterDiscount
                                : mtplPremium.premium
                        }
                        originalPrice={
                            mtplPremium.premiumAfterDiscount > 0 &&
                            mtplPremium.premiumAfterDiscount !== mtplPremium.premium
                                ? mtplPremium.premium
                                : undefined
                        }
                        discountPercentage={
                            mtplPremium.premiumAfterDiscount > 0 &&
                            mtplPremium.premiumAfterDiscount !== mtplPremium.premium
                                ? ((mtplPremium.premium - mtplPremium.premiumAfterDiscount) / mtplPremium.premium) * 100
                                : undefined
                        }
                        insuranceDuration={insuranceDuration}
                        description={t('eshop.transport.mtplBasicDescription')}
                    />
                </Card>
            )}

            {isCascoValidLongTerm && insuranceDuration === InsuranceDuration.TwelveMonths && (
                <Card className="p-6">
                    <div className="mb-4 flex flex-row gap-3">
                        <Heading level="h3">{t('eshop.transport.policyType.casco')}</Heading>
                    </div>
                    <Paragraph isGray size="small">
                        {t('eshop.transport.suchVehicleIsAlreadyInsured')}
                    </Paragraph>
                </Card>
            )}

            <ChoiceButtonInput
                name="insurancePackage"
                options={premiumOptions}
                onChange={onChange}
                defaultChecked={value}
            />
            {showCompare ? (
                <Message
                    className="md:flex-row md:items-start"
                    variant="info"
                    addon={
                        <Button
                            href={appParameters.eshopInfoUrls.comparePolicies}
                            preventDefault
                            variant="outlined"
                            size="tiny"
                            target="_blank"
                            rightAddon={<ArrowRightIcon className="size-4" />}>
                            {t('eshop.general.compare')}
                        </Button>
                    }>
                    <div className="flex flex-col gap-2">
                        <div className="font-medium">{t('eshop.general.compareInsurances')}</div>
                        <div className="text-sm">{t('eshop.transport.compareInsurancesMessage')}</div>
                    </div>
                </Message>
            ) : (
                <ContactUsCard />
            )}
        </div>
    );
}
